import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    TextField,
    Typography,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import moment from 'moment';

import { checkStatus, parseJSON, handleError } from '../../../shared/api/core';
import { Toast, ToastError, ToastSuccess } from '../../../shared/components/Notifier';
import { Location as BusinessLocation } from '../../../shared/models';
import { getProductsByPage } from '../../../products/api/Api';
import { Product } from '../../../products/models';
import {
    createExpiringStockTracker,
    getActiveKoronaPromotions,
    getExpiringStockPromoTemplates,
    getLocations,
    previewExpiringStockPromoTemplate,
} from '../../api/Api';
import { ExpiringStockPromoTemplate, ExpiringStockTrackerCreatePayload, KoronaPromotion } from '../../models';

const PROMO_PAGE_SIZE = 10;

/** Flip to true to re-enable linking an existing Korona promotion (and its API fetch). */
const LINK_EXISTING_KORONA_PROMO_ENABLED = false;

type PromoSourceMode = 'existing' | 'template' | 'none';

const formatKoronaPromotionLabel = (promo: KoronaPromotion): string => {
    const validToLabel = promo.validTo ? ` (until ${moment(promo.validTo).format('MM/DD/YYYY')})` : '';
    return `${promo.name}${validToLabel}`;
};

const extractApiErrorMessage = (error: any, fallback: string): string => {
    const data = error?.response?.data;
    if (!data) {
        return fallback;
    }
    if (typeof data === 'string') {
        return data;
    }
    if (typeof data.error === 'string') {
        return data.error;
    }
    if (typeof data.detail === 'string') {
        return data.detail;
    }
    if (typeof data.non_field_errors?.[0] === 'string') {
        return data.non_field_errors[0];
    }
    const firstKey = Object.keys(data)[0];
    const firstVal = firstKey ? data[firstKey] : null;
    if (typeof firstVal === 'string') {
        return firstVal;
    }
    if (Array.isArray(firstVal) && typeof firstVal[0] === 'string') {
        return firstVal[0];
    }
    return fallback;
};

interface Props {
    open: boolean;
    businessId: number | string;
    onClose: () => void;
    onCreated: () => void;
    showToast: (toast: Toast) => void;
}

const CreateTrackerDialog: React.FC<Props> = ({ open, businessId, onClose, onCreated, showToast }) => {
    const [productOptions, setProductOptions] = useState<Product[]>([]);
    const [productSearchLoading, setProductSearchLoading] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

    const [locations, setLocations] = useState<BusinessLocation[]>([]);
    const [selectedLocation, setSelectedLocation] = useState<BusinessLocation | null>(null);

    const [initialCount, setInitialCount] = useState<string>('');
    const [expiryDate, setExpiryDate] = useState<string>('');
    const [notes, setNotes] = useState<string>('');

    const [promoSourceMode, setPromoSourceMode] = useState<PromoSourceMode>('template');

    const [koronaPromotions, setKoronaPromotions] = useState<KoronaPromotion[]>([]);
    const [koronaPromotionsLoading, setKoronaPromotionsLoading] = useState(false);
    const [promoSearch, setPromoSearch] = useState('');
    const [promoVisibleCount, setPromoVisibleCount] = useState(PROMO_PAGE_SIZE);
    const [selectedKoronaPromo, setSelectedKoronaPromo] = useState<KoronaPromotion | null>(null);

    const [templates, setTemplates] = useState<ExpiringStockPromoTemplate[]>([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState<number | ''>('');
    const [previewName, setPreviewName] = useState<string>('');
    const [previewLoading, setPreviewLoading] = useState(false);
    const [manualPromoName, setManualPromoName] = useState<string>('');

    const [isSaving, setSaving] = useState(false);
    const productSearchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const productSearchRequestIdRef = useRef(0);
    const productInputRef = useRef<HTMLInputElement | null>(null);

    const filteredPromotions = useMemo(() => {
        const term = promoSearch.trim().toLowerCase();
        const sorted = [...koronaPromotions].sort(
            (left, right) => (right.revision ?? 0) - (left.revision ?? 0)
        );
        if (!term) {
            return sorted;
        }
        return sorted.filter(
            (promo) =>
                promo.name.toLowerCase().includes(term) ||
                promo.number.toLowerCase().includes(term)
        );
    }, [koronaPromotions, promoSearch]);

    const visiblePromotions = useMemo(() => {
        const sliced = filteredPromotions.slice(0, promoVisibleCount);
        if (
            selectedKoronaPromo &&
            !sliced.some((promo) => promo.id === selectedKoronaPromo.id)
        ) {
            return [selectedKoronaPromo, ...sliced];
        }
        return sliced;
    }, [filteredPromotions, promoVisibleCount, selectedKoronaPromo]);

    useEffect(() => {
        if (!open || !businessId) return;
        getLocations(businessId)
            .catch(handleError)
            .then(checkStatus)
            .then(parseJSON)
            .then((data: any) => setLocations(Array.isArray(data) ? data : data?.results || []))
            .catch(() => showToast({ message: 'Failed to load locations.', options: ToastError }));

        getExpiringStockPromoTemplates(businessId)
            .catch(handleError)
            .then(checkStatus)
            .then(parseJSON)
            .then((data: any) => setTemplates(data?.results || []))
            .catch(() => showToast({ message: 'Failed to load promo templates.', options: ToastError }));

        if (LINK_EXISTING_KORONA_PROMO_ENABLED) {
            setKoronaPromotionsLoading(true);
            getActiveKoronaPromotions(businessId)
                .catch(handleError)
                .then(checkStatus)
                .then(parseJSON)
                .then((data: KoronaPromotion[]) => setKoronaPromotions(data || []))
                .catch(() =>
                    showToast({ message: 'Failed to load Korona promotions.', options: ToastError })
                )
                .finally(() => setKoronaPromotionsLoading(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, businessId]);

    useEffect(() => {
        if (!open) {
            return;
        }
        const focusTimeout = window.setTimeout(() => {
            productInputRef.current?.focus();
        }, 50);
        return () => window.clearTimeout(focusTimeout);
    }, [open]);

    useEffect(() => {
        return () => {
            if (productSearchDebounceRef.current) {
                clearTimeout(productSearchDebounceRef.current);
            }
            productSearchRequestIdRef.current += 1;
        };
    }, []);

    const searchProducts = (term: string) => {
        if (productSearchDebounceRef.current) {
            clearTimeout(productSearchDebounceRef.current);
            productSearchDebounceRef.current = null;
        }

        const trimmed = term.trim();
        if (!businessId || !trimmed) {
            productSearchRequestIdRef.current += 1;
            setProductOptions([]);
            setProductSearchLoading(false);
            return;
        }

        productSearchDebounceRef.current = setTimeout(() => {
            const requestId = ++productSearchRequestIdRef.current;
            setProductSearchLoading(true);
            getProductsByPage(1, 20, trimmed, businessId)
                .catch(handleError)
                .then(checkStatus)
                .then(parseJSON)
                .then((data: any) => {
                    if (requestId === productSearchRequestIdRef.current) {
                        setProductOptions(data?.results || []);
                    }
                })
                .finally(() => {
                    if (requestId === productSearchRequestIdRef.current) {
                        setProductSearchLoading(false);
                    }
                });
        }, 400);
    };

    const handlePromoInputChange = (_: unknown, value: string, reason: string) => {
        if (reason === 'input' || reason === 'clear') {
            setPromoSearch(value);
            setPromoVisibleCount(PROMO_PAGE_SIZE);
        }
    };

    const handlePromoListScroll = (event: React.SyntheticEvent) => {
        const listbox = event.currentTarget;
        const isNearBottom =
            listbox.scrollTop + listbox.clientHeight >= listbox.scrollHeight - 24;
        if (!isNearBottom) {
            return;
        }
        setPromoVisibleCount((count) => {
            if (count >= filteredPromotions.length) {
                return count;
            }
            return count + PROMO_PAGE_SIZE;
        });
    };

    const handlePromoSourceModeChange = (mode: PromoSourceMode) => {
        if (mode === 'existing' && !LINK_EXISTING_KORONA_PROMO_ENABLED) {
            return;
        }
        setPromoSourceMode(mode);
        if (mode === 'existing') {
            setSelectedTemplateId('');
            setPreviewName('');
        } else if (mode === 'template') {
            setSelectedKoronaPromo(null);
            setPromoSearch('');
            setManualPromoName('');
        } else {
            setSelectedTemplateId('');
            setPreviewName('');
            setSelectedKoronaPromo(null);
            setPromoSearch('');
            setManualPromoName('');
        }
    };

    useEffect(() => {
        if (promoSourceMode !== 'template' || !selectedTemplateId || !selectedProduct) {
            setPreviewName('');
            return;
        }
        setPreviewLoading(true);
        previewExpiringStockPromoTemplate(Number(selectedTemplateId), {
            product_id: selectedProduct.uuid,
            location_id: selectedLocation?.id || null,
            product_expiry_date: expiryDate || null,
        })
            .catch(handleError)
            .then(checkStatus)
            .then(parseJSON)
            .then((data: any) => setPreviewName(data?.promo_name || ''))
            .catch(() => setPreviewName(''))
            .finally(() => setPreviewLoading(false));
    }, [promoSourceMode, selectedTemplateId, selectedProduct, selectedLocation, expiryDate]);

    const resetForm = () => {
        if (productSearchDebounceRef.current) {
            clearTimeout(productSearchDebounceRef.current);
            productSearchDebounceRef.current = null;
        }
        productSearchRequestIdRef.current += 1;
        setProductOptions([]);
        setProductSearchLoading(false);
        setSelectedProduct(null);
        setSelectedLocation(null);
        setInitialCount('');
        setExpiryDate('');
        setNotes('');
        setPromoSourceMode('template');
        setSelectedTemplateId('');
        setSelectedKoronaPromo(null);
        setPromoSearch('');
        setPromoVisibleCount(PROMO_PAGE_SIZE);
        setPreviewName('');
        setManualPromoName('');
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const hasValidPromotion =
        promoSourceMode === 'none' ||
        (promoSourceMode === 'template' && !!selectedTemplateId) ||
        (promoSourceMode === 'existing' && !!selectedKoronaPromo);

    const isValid =
        !!selectedProduct &&
        !!selectedLocation &&
        !!initialCount &&
        Number(initialCount) > 0 &&
        !!expiryDate &&
        hasValidPromotion;

    const handleInitialCountChange = (value: string) => {
        if (value === '') {
            setInitialCount('');
            return;
        }
        const parsed = Number(value);
        if (!Number.isNaN(parsed) && parsed >= 1) {
            setInitialCount(value);
        }
    };

    const handleSave = () => {
        if (!selectedProduct || !selectedLocation || !isValid) return;
        setSaving(true);

        const payload: ExpiringStockTrackerCreatePayload = {
            business: Number(businessId),
            product: selectedProduct.uuid,
            location: Number(selectedLocation.id),
            initial_expiring_count: Number(initialCount),
            product_expiry_date: expiryDate,
            notes: notes || null,
        };

        if (promoSourceMode === 'existing' && selectedKoronaPromo) {
            payload.promo_korona_uuid = selectedKoronaPromo.id;
            payload.promo_template = null;
            if (manualPromoName) {
                payload.promo_name = manualPromoName;
            }
        } else if (promoSourceMode === 'template' && selectedTemplateId) {
            payload.promo_template = Number(selectedTemplateId);
            payload.promo_korona_uuid = null;
        } else {
            payload.promo_template = null;
            payload.promo_korona_uuid = null;
        }

        createExpiringStockTracker(payload)
            .catch(handleError)
            .then(checkStatus)
            .then(parseJSON)
            .then(() => {
                showToast({ message: 'Tracker created successfully.', options: ToastSuccess });
                resetForm();
                onCreated();
            })
            .catch((error: any) => {
                const message = extractApiErrorMessage(error, 'Failed to create tracker.');
                showToast({ message, options: ToastError });
            })
            .finally(() => setSaving(false));
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Create Expiring Stock Tracker</DialogTitle>
            <DialogContent>
                <Autocomplete<Product, false, false, false>
                    options={productOptions}
                    value={selectedProduct}
                    getOptionLabel={(option) => `${option.name} (${option.number})`}
                    getOptionSelected={(option, value) => option.uuid === value?.uuid}
                    loading={productSearchLoading}
                    filterOptions={(options) => options}
                    onChange={(_, value) => setSelectedProduct(value)}
                    onInputChange={(_, value, reason) => {
                        if (reason === 'input' || reason === 'clear') {
                            searchProducts(value);
                        }
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            inputRef={productInputRef}
                            label="Product"
                            variant="outlined"
                            margin="normal"
                            fullWidth
                            required
                            autoFocus
                        />
                    )}
                />
                <Autocomplete<BusinessLocation, false, false, false>
                    options={locations}
                    value={selectedLocation}
                    getOptionLabel={(option) => option.name || ''}
                    getOptionSelected={(option, value) => option.id === value?.id}
                    onChange={(_, value) => setSelectedLocation(value)}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Location"
                            variant="outlined"
                            margin="normal"
                            fullWidth
                            required
                        />
                    )}
                />
                <TextField
                    label="Initial Expiring Count"
                    type="number"
                    value={initialCount}
                    onChange={(e) => handleInitialCountChange(e.target.value)}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    required
                    inputProps={{ min: 1 }}
                />
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                    <KeyboardDatePicker
                        fullWidth
                        required
                        label="Tracker Expiry Date"
                        format="yyyy-MM-dd"
                        inputVariant="outlined"
                        margin="normal"
                        value={expiryDate ? moment(expiryDate, 'YYYY-MM-DD').toDate() : null}
                        onChange={(date) =>
                            setExpiryDate(date ? moment(date).format('YYYY-MM-DD') : '')
                        }
                        autoOk
                        openTo="date"
                        views={['year', 'month', 'date']}
                    />
                </MuiPickersUtilsProvider>

                <Typography variant="subtitle2" style={{ marginTop: 16 }}>
                    Promotion source
                </Typography>
                <FormControl component="fieldset" margin="dense">
                    <RadioGroup
                        row
                        value={promoSourceMode}
                        onChange={(e) => handlePromoSourceModeChange(e.target.value as PromoSourceMode)}
                    >
                        <FormControlLabel
                            value="template"
                            control={<Radio color="primary" />}
                            label="Create from promo template"
                        />
                        <FormControlLabel
                            value="existing"
                            control={<Radio color="primary" />}
                            label="Link existing Korona promotion"
                            disabled={!LINK_EXISTING_KORONA_PROMO_ENABLED}
                        />
                        <FormControlLabel
                            value="none"
                            control={<Radio color="primary" />}
                            label="No promotion"
                        />
                    </RadioGroup>
                </FormControl>

                {promoSourceMode === 'existing' ? (
                    <>
                        <Autocomplete<KoronaPromotion, false, false, false>
                            options={visiblePromotions}
                            value={selectedKoronaPromo}
                            getOptionLabel={formatKoronaPromotionLabel}
                            getOptionSelected={(option, value) => option.id === value?.id}
                            loading={koronaPromotionsLoading}
                            filterOptions={(options) => options}
                            onChange={(_, value) => setSelectedKoronaPromo(value)}
                            onInputChange={handlePromoInputChange}
                            ListboxProps={{
                                style: { maxHeight: 280 },
                                onScroll: handlePromoListScroll,
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Korona Promotion"
                                    variant="outlined"
                                    margin="normal"
                                    fullWidth
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {koronaPromotionsLoading ? (
                                                    <CircularProgress color="inherit" size={20} />
                                                ) : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                        <Typography variant="caption" color="textSecondary" display="block">
                            The selected product must already be included on this Korona promotion. The tracker
                            deactivates it automatically once stock is depleted or the validity date passes.
                        </Typography>
                        <TextField
                            label="Promo Name (optional)"
                            value={manualPromoName}
                            onChange={(e) => setManualPromoName(e.target.value)}
                            fullWidth
                            margin="normal"
                            variant="outlined"
                            helperText="Leave blank to use the Korona promotion name."
                        />
                    </>
                ) : promoSourceMode === 'template' ? (
                    <>
                        <FormControl variant="outlined" fullWidth margin="normal">
                            <InputLabel id="tracker-template-label">Promo Template</InputLabel>
                            <Select
                                labelId="tracker-template-label"
                                label="Promo Template"
                                value={selectedTemplateId}
                                onChange={(e) => setSelectedTemplateId(e.target.value as number)}
                            >
                                <MenuItem value="">Select a template</MenuItem>
                                {templates.map((t) => (
                                    <MenuItem key={t.id} value={t.id}>
                                        {t.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Typography variant="caption" color="textSecondary" display="block">
                            A new Korona promotion will be created from this template for the selected product.
                            The tracker deactivates that promotion when stock is depleted or validity ends.
                        </Typography>
                        {selectedTemplateId ? (
                            <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 8 }}>
                                {previewLoading ? 'Generating preview...' : `Preview name: ${previewName || '—'}`}
                            </Typography>
                        ) : null}
                    </>
                ) : (
                    <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 8 }}>
                        Track expiring inventory without creating or linking a Korona promotion. Sales are still
                        attributed to this tracker; it closes when stock is depleted or the product expiry date
                        passes.
                    </Typography>
                )}

                <TextField
                    label="Notes (optional)"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    multiline
                    rows={2}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={isSaving}>
                    Cancel
                </Button>
                <Button onClick={handleSave} color="primary" variant="contained" disabled={!isValid || isSaving}>
                    {isSaving ? 'Creating...' : 'Create Tracker'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreateTrackerDialog;
